'use client'

import { useAtomValue } from 'jotai'
import { useTranslations } from 'next-intl'
import { useCallback, useRef, useState } from 'react'
import {
	buildQrMarkup,
	DOWNLOAD_QR_ACTION_TYPES,
	downloadQRCode,
} from 'utils/downloadQrCode'

import {
	type BulkDownloadFormat,
	DEFAULT_BULK_DOWNLOAD_FORMAT,
	SUPPORTED_BULK_DOWNLOAD_FORMATS,
} from '@/constants'
import { TQrData } from '@/generator/shared'
import { userAtom } from '@/global-state'
import { formatInTimezone, getUserTimezone } from '@/utils/timezoneUtils'

type FileFormat = BulkDownloadFormat

const getBulkDownloadFormat = (): FileFormat => {
	const configFormat =
		typeof window !== 'undefined'
			? (window as unknown as Record<string, unknown>).QR_BULK_DOWNLOAD_FORMAT
			: undefined

	if (
		configFormat &&
		SUPPORTED_BULK_DOWNLOAD_FORMATS.includes(configFormat as BulkDownloadFormat)
	) {
		return configFormat as FileFormat
	}

	const envFormat = process.env.NEXT_PUBLIC_BULK_DOWNLOAD_FORMAT
	if (
		envFormat &&
		SUPPORTED_BULK_DOWNLOAD_FORMATS.includes(envFormat as BulkDownloadFormat)
	) {
		return envFormat as FileFormat
	}

	return DEFAULT_BULK_DOWNLOAD_FORMAT
}

export type BulkDownloadState = {
	isDownloading: boolean
	progress: string
	isComplete: boolean
}

const createZipFromBlobs = async (
	files: {
		name: string
		blob: Blob
	}[],
): Promise<Blob> => {
	const encoder = new TextEncoder()
	const zipParts: Uint8Array[] = []
	const centralDirectory: Uint8Array[] = []
	let offset = 0

	const crcTable = new Uint32Array(256)
	for (let i = 0; i < 256; i++) {
		let crc = i
		for (let j = 0; j < 8; j++) {
			crc = crc & 1 ? 0xedb88320 ^ (crc >>> 1) : crc >>> 1
		}
		crcTable[i] = crc
	}

	const crc32 = (data: Uint8Array): number => {
		let crc = 0xffffffff
		for (let i = 0; i < data.length; i++) {
			const byteValue = data[i]
			if (byteValue === undefined) {
				throw new Error('Invalid byte data at index ' + i)
			}
			const tableValue = crcTable[(crc ^ byteValue) & 0xff]
			if (tableValue === undefined) {
				throw new Error('CRC table lookup failed')
			}
			crc = (crc >>> 8) ^ tableValue
		}
		return (crc ^ 0xffffffff) >>> 0
	}

	for (const file of files) {
		const nameBytes = encoder.encode(file.name)
		const fileData = new Uint8Array(await file.blob.arrayBuffer())

		const localHeader = new Uint8Array(30 + nameBytes.length)
		const view = new DataView(localHeader.buffer)

		view.setUint32(0, 0x04034b50, true)
		view.setUint16(4, 10, true)
		view.setUint32(14, crc32(fileData), true)
		view.setUint32(18, fileData.length, true)
		view.setUint32(22, fileData.length, true)
		view.setUint16(26, nameBytes.length, true)

		localHeader.set(nameBytes, 30)
		zipParts.push(localHeader, fileData)

		const centralHeader = new Uint8Array(46 + nameBytes.length)
		const centralView = new DataView(centralHeader.buffer)

		centralView.setUint32(0, 0x02014b50, true)
		centralView.setUint16(4, 10, true)
		centralView.setUint16(6, 10, true)
		centralView.setUint32(16, crc32(fileData), true)
		centralView.setUint32(20, fileData.length, true)
		centralView.setUint32(24, fileData.length, true)
		centralView.setUint16(28, nameBytes.length, true)
		centralView.setUint32(42, offset, true)

		centralHeader.set(nameBytes, 46)
		centralDirectory.push(centralHeader)
		offset += localHeader.length + fileData.length
	}

	const centralDirData = new Uint8Array(
		centralDirectory.reduce((acc, arr) => acc + arr.length, 0),
	)
	let centralOffset = 0
	for (const arr of centralDirectory) {
		centralDirData.set(arr, centralOffset)
		centralOffset += arr.length
	}

	const endRecord = new Uint8Array(22)
	const endView = new DataView(endRecord.buffer)
	endView.setUint32(0, 0x06054b50, true)
	endView.setUint16(8, files.length, true)
	endView.setUint16(10, files.length, true)
	endView.setUint32(12, centralDirData.length, true)
	endView.setUint32(16, offset, true)

	const totalLength = [...zipParts, centralDirData, endRecord].reduce(
		(acc, arr) => acc + arr.length,
		0,
	)
	const result = new Uint8Array(totalLength)
	let resultOffset = 0

	for (const part of [...zipParts, centralDirData, endRecord]) {
		result.set(part, resultOffset)
		resultOffset += part.length
	}

	return new Blob([result], {
		type: 'application/zip',
	})
}

const getSvgDimensions = (svg: string) => {
	const svgDoc = new DOMParser().parseFromString(svg, 'image/svg+xml')
	const svgEl = svgDoc.querySelector('svg')
	const width = svgEl?.getAttribute('width')
		? parseInt(svgEl.getAttribute('width')!)
		: 320
	const height = svgEl?.getAttribute('height')
		? parseInt(svgEl.getAttribute('height')!)
		: 320
	return {
		width,
		height,
	}
}

const convertSvgToBlob = async (
	svgString: string,
	format: FileFormat,
): Promise<Blob> => {
	if (format === 'svg') {
		return new Blob([svgString], {
			type: 'image/svg+xml',
		})
	}

	return new Promise((resolve, reject) => {
		const canvas = document.createElement('canvas')
		const ctx = canvas.getContext('2d')
		const img = new Image()

		img.onload = () => {
			const { width, height } = getSvgDimensions(svgString)
			const deviceRatio = window?.devicePixelRatio || 1
			const scaleFactor = deviceRatio * 2
			canvas.width = width * scaleFactor
			canvas.height = height * scaleFactor

			if (format === 'jpeg') {
				ctx!.fillStyle = '#ffffff'
				ctx!.fillRect(0, 0, canvas.width, canvas.height)
			}

			ctx!.scale(scaleFactor, scaleFactor)
			ctx!.drawImage(img, 0, 0, width, height)

			canvas.toBlob(
				(blob) => {
					if (blob) {
						resolve(blob)
					} else {
						reject(new Error('Canvas conversion failed'))
					}
				},
				`image/${format}`,
				0.95,
			)
		}

		img.onerror = () => reject(new Error('SVG load failed'))
		img.src = URL.createObjectURL(
			new Blob([svgString], {
				type: 'image/svg+xml',
			}),
		)
	})
}

export const useBulkDownload = () => {
	const t = useTranslations()
	const user = useAtomValue(userAtom)
	const [downloadState, setDownloadState] = useState<BulkDownloadState>({
		isDownloading: false,
		progress: '',
		isComplete: false,
	})

	const cancelRef = useRef(false)

	const downloadSelectedQrCodes = useCallback(
		async (selectedQrs: TQrData[], format?: FileFormat) => {
			const downloadFormat = format || getBulkDownloadFormat()
			if (selectedQrs.length === 0) return

			if (selectedQrs.length === 1) {
				const firstQr = selectedQrs[0]
				if (!firstQr) {
					return
				}
				const extensionMap: Record<FileFormat, DOWNLOAD_QR_ACTION_TYPES> = {
					svg: 'downloadAsSvg',
					png: 'downloadAsPng',
					jpeg: 'downloadAsJpg',
				}
				await downloadQRCode(firstQr, extensionMap[downloadFormat])
				return
			}

			cancelRef.current = false
			setDownloadState({
				isDownloading: true,
				progress: t('dashboard__bulk_download__preparing'),
				isComplete: false,
			})

			try {
				const batchSize = 3
				const files: {
					name: string
					blob: Blob
				}[] = []
				const nameCount = new Map<string, number>()
				for (const qr of selectedQrs) {
					const key = (qr.name || qr.id).toLowerCase()
					nameCount.set(key, (nameCount.get(key) || 0) + 1)
				}
				let successfulDownloads = 0

				for (let i = 0; i < selectedQrs.length; i += batchSize) {
					if (cancelRef.current) {
						setDownloadState({
							isDownloading: false,
							progress: t('dashboard__bulk_download__cancelled'),
							isComplete: false,
						})
						return
					}

					const batch = selectedQrs.slice(i, i + batchSize)
					const batchEnd = Math.min(i + batchSize, selectedQrs.length)

					setDownloadState({
						isDownloading: true,
						progress: t('dashboard__bulk_download__processing_batch', {
							start: i + 1,
							end: batchEnd,
							total: selectedQrs.length,
						}),
						isComplete: false,
					})

					const batchPromises = batch.map(async (qrData) => {
						const baseName = qrData.name || qrData.id
						try {
							if (!qrData.qrDesign || !qrData.content) return null

							const svgString = await buildQrMarkup(qrData)
							if (!svgString?.trim()) return null

							const blob = await convertSvgToBlob(svgString, downloadFormat)
							const isDuplicate =
								(nameCount.get(baseName.toLowerCase()) || 0) > 1
							const fileName = isDuplicate
								? `${baseName}-${qrData.uri || qrData.id}.${downloadFormat}`
								: `${baseName}.${downloadFormat}`

							return {
								name: fileName,
								blob,
							}
						} catch (error) {
							console.error(
								`Failed to generate QR code for ${baseName}:`,
								error,
							)
							return null
						}
					})

					const batchResults = await Promise.allSettled(batchPromises)

					for (const result of batchResults) {
						if (result.status === 'fulfilled' && result.value) {
							files.push(result.value)
							successfulDownloads++
						}
					}
				}

				if (!cancelRef.current && successfulDownloads > 0) {
					setDownloadState({
						isDownloading: true,
						progress: t('dashboard__bulk_download__creating_zip'),
						isComplete: false,
					})

					const zipBlob = await createZipFromBlobs(files)
					const userTimezone = user?.timezone || getUserTimezone()
					const timestamp = formatInTimezone(
						new Date(),
						'yyyy-MM-dd_HH-mm-ss',
						userTimezone,
					)
					const zipFileName = `QR_Codes_Bulk_Download_${timestamp}.zip`

					const url = URL.createObjectURL(zipBlob)
					const link = document.createElement('a')
					link.href = url
					link.download = zipFileName
					link.click()
					URL.revokeObjectURL(url)

					setDownloadState({
						isDownloading: false,
						progress: t('dashboard__bulk_download__success_count', {
							count: successfulDownloads,
						}),
						isComplete: true,
					})
				} else if (!cancelRef.current) {
					setDownloadState({
						isDownloading: false,
						progress: t('dashboard__bulk_download__no_codes_processed'),
						isComplete: false,
					})
				}
			} catch (error) {
				console.error('Bulk download failed:', error)
				setDownloadState({
					isDownloading: false,
					progress: t('dashboard__bulk_download__failed'),
					isComplete: false,
				})
			}
		},
		[t, user?.timezone],
	)

	const cancelDownload = useCallback(() => {
		cancelRef.current = true
		setDownloadState({
			isDownloading: false,
			progress: t('dashboard__bulk_download__cancelled'),
			isComplete: false,
		})
	}, [t])

	const resetDownloadState = useCallback(() => {
		setDownloadState({
			isDownloading: false,
			progress: '',
			isComplete: false,
		})
	}, [])

	return {
		downloadState,
		downloadSelectedQrCodes,
		cancelDownload,
		resetDownloadState,
	}
}
